import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, Users, Package, ShoppingCart, Target, LogOut,
  Plus, RefreshCw, Settings, Bot, ChevronRight, Calendar, ArrowUpRight, ArrowDownRight,
  Building2, BarChart3, PieChart, FileText, Repeat, Wallet, Clock, Receipt, Calculator, Boxes,
  Menu, ChevronLeft, Loader2, Edit, Trash2, Check, X, MoreHorizontal, AlertTriangle,
  SlidersHorizontal, ArrowUpDown, ArrowUp, ArrowDown, Search, CreditCard, Banknote, ArrowRightLeft,
  ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO, isAfter, isBefore, isToday } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

// Month keys for translation
const MONTH_KEYS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

// Helper to format dates in UTC to avoid timezone conversion issues
const formatDateUTC = (dateString, formatStr = "MMM d, yyyy") => {
  if (!dateString) return "-";
  try {
    return formatInTimeZone(parseISO(dateString), "UTC", formatStr);
  } catch {
    return format(parseISO(dateString), formatStr);
  }
};

export default function CompanyDashboard() {
  const { user, logout } = useAuth();
  const { brandName } = useBrand();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [salesSummary, setSalesSummary] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [productsServices, setProductsServices] = useState([]);
  const [disabledPages, setDisabledPages] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Transaction-related state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [transactionOverview, setTransactionOverview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [paymentMethodBalances, setPaymentMethodBalances] = useState([]);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  
  // Column visibility state for transactions table
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    name: true,
    category: true,
    paymentMethod: true,
    type: true,
    original: true,
    converted: true,
    balance: true,
    status: true,
    actions: true
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [salesRes, clientsRes, objectivesRes, recentSalesRes, productsRes, disabledPagesRes] = await Promise.all([
        api.get("/sales/summary"),
        api.get("/clients/stats"),
        api.get("/financial-objectives/summary"),
        api.get("/sales?limit=5"),
        api.get("/products-services"),
        api.get("/company-pages/disabled").catch(() => ({ data: { disabled_pages: [] } }))
      ]);
      
      setSalesSummary(salesRes.data);
      setClientStats(clientsRes.data);
      setObjectives(objectivesRes.data.objectives || []);
      setRecentSales(recentSalesRes.data.slice(0, 5));
      setProductsServices(productsRes.data);
      setDisabledPages(disabledPagesRes.data?.disabled_pages || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("This feature is only available for business accounts");
        navigate("/dashboard");
      } else {
        toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, t]);
  
  // Fetch transaction data separately (depends on selected month/year)
  const fetchTransactionData = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const [overviewRes, categoriesRes, currenciesRes, settingsRes, companySettingsRes, paymentBalancesRes] = await Promise.all([
        api.get(`/overview/month?year=${selectedYear}&month=${selectedMonth}`),
        api.get("/categories"),
        api.get("/currencies"),
        api.get("/settings"),
        api.get("/company-settings").catch(() => ({ data: null })),
        api.get("/analytics/payment-methods").catch(() => ({ data: [] }))
      ]);
      setTransactionOverview(overviewRes.data);
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
      setUserSettings(settingsRes.data);
      setCompanySettings(companySettingsRes.data);
      setPaymentMethodBalances(paymentBalancesRes.data?.payment_methods || []);
    } catch (error) {
      console.error("Error fetching transaction data:", error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [selectedYear, selectedMonth]);
  
  // Month navigation handlers
  const handlePreviousMonth = () => {
    setSelectedRows(new Set()); // Clear selection on month change
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedRows(new Set()); // Clear selection on month change
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };
  
  // Column toggle
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };
  
  // Sorting handler
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Currency helpers
  const preferredCurrency = userSettings?.preferred_currency;
  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || '$';
  };
  
  // Get sorted and filtered transactions
  const getSortedTransactions = () => {
    if (!transactionOverview?.transactions) return [];
    
    let filtered = [...transactionOverview.transactions];
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tx => 
        tx.name?.toLowerCase().includes(query) ||
        tx.category_name?.toLowerCase().includes(query) ||
        tx.description?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle special cases
        if (sortConfig.key === 'category') {
          aVal = a.category_name || '';
          bVal = b.category_name || '';
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  };

  // Calculate running balance for sorted transactions
  const getTransactionsWithBalance = () => {
    const sorted = getSortedTransactions();
    let runningBalance = 0;
    
    // Calculate from oldest to newest for proper running balance
    const chronological = [...sorted].sort((a, b) => new Date(a.date) - new Date(b.date));
    const balanceMap = {};
    
    chronological.forEach(tx => {
      if (tx.status !== 'skipped') {
        const amount = preferredCurrency && tx.converted_amount != null 
          ? tx.converted_amount 
          : tx.amount;
        runningBalance += tx.type === 'income' ? amount : -amount;
      }
      balanceMap[tx.id] = runningBalance;
    });
    
    // Return in display order with running balance
    return sorted.map(tx => ({
      ...tx,
      runningBalance: balanceMap[tx.id] || 0
    }));
  };

  // Toggle single row selection
  const toggleRowSelection = (txId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txId)) {
        newSet.delete(txId);
      } else {
        newSet.add(txId);
      }
      return newSet;
    });
  };

  // Toggle select all visible rows
  const toggleSelectAll = () => {
    const transactions = getTransactionsWithBalance();
    if (selectedRows.size === transactions.length && transactions.length > 0) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(transactions.map(tx => tx.id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedRows(new Set());
  };

  // Calculate totals for selected rows
  const getSelectionTotals = () => {
    const transactions = getTransactionsWithBalance();
    const selected = transactions.filter(tx => selectedRows.has(tx.id));
    
    let totalIncome = 0;
    let totalExpenses = 0;
    
    selected.forEach(tx => {
      // Use converted_amount if available (multi-currency), otherwise use amount
      const amount = (preferredCurrency && tx.converted_amount != null) 
        ? tx.converted_amount 
        : tx.amount;
      
      if (tx.type === 'income') {
        totalIncome += amount;
      } else if (tx.type === 'expense') {
        totalExpenses += amount;
      }
    });
    
    return {
      count: selected.length,
      totalIncome,
      totalExpenses,
      balance: totalIncome - totalExpenses
    };
  };

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setShowTransactionDialog(true);
  };

  const handleEditTransaction = (tx) => {
    setEditingTransaction(tx);
    setShowTransactionDialog(true);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch transaction data when month/year changes
  useEffect(() => {
    fetchTransactionData();
  }, [fetchTransactionData]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getObjectiveIcon = (type) => {
    switch (type) {
      case "revenue_target": return DollarSign;
      case "client_acquisition": return Users;
      case "sales_volume": return ShoppingCart;
      case "profit_margin": return TrendingUp;
      case "expense_reduction": return TrendingDown;
      default: return Target;
    }
  };

  const getStatusColor = (progress, daysRemaining) => {
    if (progress >= 100) return "bg-green-500";
    if (daysRemaining <= 0) return "bg-red-500";
    if (progress >= 75) return "bg-green-500";
    if (progress >= 50) return "bg-yellow-500";
    return "bg-orange-500";
  };

  // Helper to check if a page is enabled (not disabled by admin)
  const isPageEnabled = (pageId) => !disabledPages.includes(pageId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
              <Skeleton className="h-6 w-48" />
            </div>
            <Skeleton className="h-9 w-24" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Navigation menu items for hamburger menu
  const navigationItems = [
    { id: "products-services", icon: Package, label: "products_services", fallback: "Products & Services", path: "/products-services", color: "blue" },
    { id: "clients", icon: Users, label: "clients", fallback: "Clients", path: "/clients", color: "green" },
    { id: "sales", icon: ShoppingCart, label: "sales", fallback: "Sales", path: "/sales", color: "purple" },
    { id: "objectives", icon: Target, label: "objectives", fallback: "Objectives", path: "/objectives", color: "orange" },
    { id: "analytics", icon: BarChart3, label: "analytics", fallback: "Analytics", path: "/analytics", color: "cyan", conditional: "analytics" },
    { id: "invoices", icon: FileText, label: "invoices", fallback: "Invoices", path: "/invoices", color: "emerald" },
    { id: "invoice-templates", icon: FileText, label: "invoice_templates", fallback: "Invoice Templates", path: "/invoice-templates", color: "emerald" },
    { id: "recurring-revenue", icon: Repeat, label: "recurring", fallback: "Recurring Revenue", path: "/recurring-revenue", color: "violet" },
    { id: "cash-flow", icon: Wallet, label: "cash_flow", fallback: "Cash Flow", path: "/cash-flow", color: "amber", conditional: "cash-flow" },
    { id: "payment-aging", icon: Clock, label: "payment_aging", fallback: "Payment Aging", path: "/payment-aging", color: "red", conditional: "payment-aging" },
    { id: "tax-management", icon: Receipt, label: "tax_management", fallback: "Tax Management", path: "/tax-management", color: "purple", conditional: "tax-management" },
    { id: "break-even", icon: Calculator, label: "break_even", fallback: "Break-even", path: "/break-even", color: "teal", conditional: "break-even" },
    { id: "inventory", icon: Boxes, label: "inventory", fallback: "Inventory", path: "/inventory", color: "indigo", conditional: "inventory", industryExclude: "service" },
    { id: "time-tracking", icon: Clock, label: "time_tracking", fallback: "Time Tracking", path: "/time-tracking", color: "cyan", conditional: "time-tracking", industryExclude: "product" },
    { id: "product-governance", icon: ClipboardList, label: "product_governance", fallback: "Product Governance", path: "/product-governance", color: "slate", conditional: "product-governance" }
  ];

  // Filter navigation items based on conditions
  const filteredNavigationItems = navigationItems.filter(item => {
    // Check if page is disabled by admin
    if (item.conditional && !isPageEnabled(item.conditional)) return false;
    // Check industry exclusion
    if (item.industryExclude && user?.company_industry === item.industryExclude) return false;
    return true;
  });

  const handleNavigation = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  // Color classes mapping
  const colorClasses = {
    blue: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-600 dark:text-blue-400" },
    green: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-600 dark:text-green-400" },
    purple: { bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-600 dark:text-purple-400" },
    orange: { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-600 dark:text-orange-400" },
    cyan: { bg: "bg-cyan-100 dark:bg-cyan-900/30", text: "text-cyan-600 dark:text-cyan-400" },
    emerald: { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-600 dark:text-emerald-400" },
    violet: { bg: "bg-violet-100 dark:bg-violet-900/30", text: "text-violet-600 dark:text-violet-400" },
    amber: { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-600 dark:text-amber-400" },
    red: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-600 dark:text-red-400" },
    teal: { bg: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-600 dark:text-teal-400" },
    indigo: { bg: "bg-indigo-100 dark:bg-indigo-900/30", text: "text-indigo-600 dark:text-indigo-400" },
    rose: { bg: "bg-rose-100 dark:bg-rose-900/30", text: "text-rose-600 dark:text-rose-400" },
    slate: { bg: "bg-slate-100 dark:bg-slate-900/30", text: "text-slate-600 dark:text-slate-400" }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="company-dashboard">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Hamburger Menu */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="hamburger-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[350px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                    {t("navigation", "common") || "Navigation"}
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-2">
                  {filteredNavigationItems.map((item) => {
                    const Icon = item.icon;
                    const colors = colorClasses[item.color] || colorClasses.blue;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                        data-testid={`nav-menu-${item.id}`}
                      >
                        <div className={`p-2 rounded-lg ${colors.bg}`}>
                          <Icon className={`h-4 w-4 ${colors.text}`} />
                        </div>
                        <span className="font-medium text-sm">
                          {t(item.label, "company") || item.fallback}
                        </span>
                        <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
            
            <Building2 className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
            <div>
              <h1 className="font-semibold text-lg">{user?.company_name || brandName}</h1>
              <p className="text-xs text-muted-foreground">{t("business_dashboard", "company") || "Business Dashboard"}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-button">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("refresh", "common") || "Refresh"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/ai-chat")} data-testid="ai-chat-button">
              <Bot className="h-4 w-4 mr-2" />
              {t("ai_assistant", "company") || "AI Assistant"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/settings")} data-testid="settings-button">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6 pb-24">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="kpi-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("total_revenue", "company") || "Total Revenue"}</p>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{formatCurrency(salesSummary?.total_revenue)}</p>
              <div className="flex items-center gap-1 mt-1">
                <Badge variant="outline" className="text-xs">
                  {formatCurrency(salesSummary?.month_revenue)} {t("this_month", "dashboard") || "this month"}
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-sales">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("total_sales", "company") || "Total Sales"}</p>
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{salesSummary?.total_sales_count || 0}</p>
              <div className="flex items-center gap-1 mt-1">
                <Badge variant="outline" className="text-xs">
                  {salesSummary?.month_sales_count || 0} {t("this_month", "dashboard") || "this month"}
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-clients">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("active_clients", "company") || "Active Clients"}</p>
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{clientStats?.active_clients || 0}</p>
              <div className="flex items-center gap-1 mt-1">
                <Badge variant="outline" className="text-xs text-green-600">
                  +{clientStats?.new_this_month || 0} {t("new_this_month", "company") || "new this month"}
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-average">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("avg_sale_value", "company") || "Avg. Sale Value"}</p>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{formatCurrency(salesSummary?.average_sale_value)}</p>
              <div className="flex items-center gap-1 mt-1">
                <Badge variant="outline" className="text-xs">
                  {salesSummary?.pending_sales || 0} {t("pending", "common") || "pending"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Financial Objectives Progress */}
          <Card data-testid="objectives-progress">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">{t("financial_objectives", "company") || "Financial Objectives"}</CardTitle>
                <CardDescription>{t("track_business_goals", "company") || "Track your business goals"}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/objectives")}>
                {t("view_all", "common") || "View All"}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {objectives.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">{t("no_objectives", "company") || "No financial objectives set"}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/objectives")}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("create_objective", "company") || "Create Objective"}
                  </Button>
                </div>
              ) : (
                objectives.slice(0, 4).map((obj) => {
                  const Icon = getObjectiveIcon(obj.objective_type);
                  return (
                    <div key={obj.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{obj.name}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {obj.progress_percentage.toFixed(0)}%
                        </span>
                      </div>
                      <Progress 
                        value={obj.progress_percentage} 
                        className="h-2"
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {obj.target_unit === "currency" 
                            ? `${formatCurrency(obj.current_value)} / ${formatCurrency(obj.target_value)}`
                            : `${obj.current_value} / ${obj.target_value} ${obj.target_unit}`
                          }
                        </span>
                        <span>{obj.days_remaining} {t("days_left", "goals") || "days left"}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Recent Sales */}
          <Card data-testid="recent-sales">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">{t("recent_sales", "company") || "Recent Sales"}</CardTitle>
                <CardDescription>{t("latest_transactions", "company") || "Your latest transactions"}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/sales")}>
                <Plus className="h-4 w-4 mr-2" />
                {t("new_sale", "company") || "New Sale"}
              </Button>
            </CardHeader>
            <CardContent>
              {recentSales.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">{t("no_sales_yet", "company") || "No sales recorded yet"}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/sales")}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("record_sale", "company") || "Record Sale"}
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("date", "common") || "Date"}</TableHead>
                      <TableHead>{t("client", "company") || "Client"}</TableHead>
                      <TableHead>{t("product_service", "company") || "Product/Service"}</TableHead>
                      <TableHead className="text-right">{t("amount", "common") || "Amount"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentSales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell className="text-sm">
                          {sale.sale_date ? format(parseISO(sale.sale_date), "MMM d") : "-"}
                        </TableCell>
                        <TableCell className="font-medium">{sale.client_name || "-"}</TableCell>
                        <TableCell className="text-muted-foreground">{sale.product_service_name || "-"}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(sale.total_amount, sale.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ==================== TRANSACTIONS SECTION ==================== */}
        <div className="space-y-6" data-testid="transactions-section">
          {/* Month Selector and Add Button */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePreviousMonth} data-testid="prev-month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[160px] text-center">
                <p className="text-lg font-semibold">
                  {t(MONTH_KEYS[selectedMonth - 1], "dashboard") || MONTH_KEYS[selectedMonth - 1]} {selectedYear}
                </p>
              </div>
              <Button variant="outline" size="icon" onClick={handleNextMonth} data-testid="next-month">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            <Button onClick={handleAddTransaction} data-testid="add-transaction-button">
              <Plus className="h-4 w-4 mr-2" />
              {t("add_transaction", "dashboard") || "Add Transaction"}
            </Button>
          </div>

          {/* Payment Method Balances */}
          {paymentMethodBalances.length > 0 && (
            <Card data-testid="payment-method-balances-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-medium">{t("payment_method_balances", "company") || "Payment Method Balances"}</CardTitle>
                    <CardDescription>{t("all_time_balance", "company") || "All-time balance by payment method"}</CardDescription>
                  </div>
                  <Wallet className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {paymentMethodBalances.map((pm) => {
                    const balance = pm.total_received || 0;
                    const isPositive = balance >= 0;
                    return (
                      <div 
                        key={pm.payment_method_id} 
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border"
                        data-testid={`pm-balance-${pm.payment_method_id}`}
                      >
                        {pm.payment_method_type === 'gateway' && <CreditCard className="h-4 w-4 text-muted-foreground" />}
                        {pm.payment_method_type === 'bank' && <Building2 className="h-4 w-4 text-muted-foreground" />}
                        {pm.payment_method_type === 'cash' && <Banknote className="h-4 w-4 text-muted-foreground" />}
                        {pm.payment_method_type === 'check' && <Receipt className="h-4 w-4 text-muted-foreground" />}
                        {!['gateway', 'bank', 'cash', 'check'].includes(pm.payment_method_type) && <Wallet className="h-4 w-4 text-muted-foreground" />}
                        <span className="text-sm font-medium">{pm.payment_method_name}</span>
                        <span className={`text-sm font-bold font-numeric ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                          {balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {pm.total_count || 0} txns
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards - Income, Expenses, Balance */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card data-testid="income-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("total_income", "dashboard") || "Total Income"}</p>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
                <p className="text-2xl font-bold mt-2 font-numeric text-green-600">
                  {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                  {(transactionOverview?.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="expenses-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("total_expenses", "dashboard") || "Total Expenses"}</p>
                  <TrendingDown className="h-4 w-4 text-red-500" />
                </div>
                <p className="text-2xl font-bold mt-2 font-numeric text-red-600">
                  {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                  {(transactionOverview?.total_expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="balance-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("balance", "dashboard") || "Balance"}</p>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className={`text-2xl font-bold mt-2 font-numeric ${(transactionOverview?.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                  {(transactionOverview?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Transactions Table */}
          <Card data-testid="transactions-table-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t("transactions", "dashboard") || "Transactions"}</CardTitle>
                  <CardDescription>
                    {t("your_transactions_for", "dashboard") || "Your transactions for"} {t(MONTH_KEYS[selectedMonth - 1], "dashboard") || MONTH_KEYS[selectedMonth - 1]} {selectedYear}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Search */}
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("search_transactions", "dashboard") || "Search transactions..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-[200px]"
                      data-testid="search-input"
                    />
                  </div>
                  
                  {/* Column visibility */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <SlidersHorizontal className="h-4 w-4 mr-2" />
                        {t("columns", "dashboard") || "Columns"}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel>{t("show_hide_columns", "dashboard") || "Show/Hide Columns"}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.entries(visibleColumns).map(([key, visible]) => (
                        <DropdownMenuCheckboxItem
                          key={key}
                          checked={visible}
                          onCheckedChange={() => toggleColumn(key)}
                        >
                          {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Manage Categories */}
                  <Button variant="outline" size="sm" onClick={() => setShowCategoryDialog(true)} data-testid="manage-categories-button">
                    {t("manage_categories", "dashboard") || "Manage Categories"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : getTransactionsWithBalance().length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">
                    {searchQuery ? (t("no_search_results", "dashboard") || "No transactions match your search") : (t("no_transactions", "dashboard") || "No transactions found")}
                  </p>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery 
                      ? <Button variant="link" onClick={() => setSearchQuery("")}>{t("clear_search", "dashboard") || "Clear Search"}</Button>
                      : (t("start_adding", "dashboard") || "Start adding your transactions to track your finances.")}
                  </p>
                  {!searchQuery && (
                    <Button onClick={handleAddTransaction} data-testid="add-first-transaction">
                      <Plus className="h-4 w-4 mr-2" />
                      {t("add_first_transaction", "dashboard") || "Add Your First Transaction"}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={getTransactionsWithBalance().length > 0 && selectedRows.size === getTransactionsWithBalance().length}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all"
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        {visibleColumns.date && (
                          <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('date')}>
                            <div className="flex items-center">
                              {t("date", "dashboard") || "Date"}
                              {sortConfig.key === 'date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />) : <ArrowUpDown className="h-3 w-3 ml-1" />}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.name && (
                          <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('name')}>
                            <div className="flex items-center">
                              {t("name", "dashboard") || "Name"}
                              {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />) : <ArrowUpDown className="h-3 w-3 ml-1" />}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.category && (
                          <TableHead>{t("category", "dashboard") || "Category"}</TableHead>
                        )}
                        {visibleColumns.type && (
                          <TableHead>{t("type", "dashboard") || "Type"}</TableHead>
                        )}
                        {visibleColumns.original && (
                          <TableHead className="text-right">{t("amount", "dashboard") || "Amount"}</TableHead>
                        )}
                        {preferredCurrency && visibleColumns.converted && (
                          <TableHead className="text-right">{t("converted", "dashboard") || "Converted"}</TableHead>
                        )}
                        {visibleColumns.balance && (
                          <TableHead className="text-right">{t("running_balance", "dashboard") || "Balance"}</TableHead>
                        )}
                        {visibleColumns.status && (
                          <TableHead>{t("status", "dashboard") || "Status"}</TableHead>
                        )}
                        {visibleColumns.actions && (
                          <TableHead className="text-right">{t("actions", "common") || "Actions"}</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getTransactionsWithBalance().map((tx) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const txDate = new Date(tx.date);
                        txDate.setHours(0, 0, 0, 0);

                        const getStatus = () => {
                          if (tx.status === 'skipped') {
                            return { label: t("skipped", "dashboard") || "Skipped", variant: "muted", icon: X };
                          }
                          if (tx.status === 'confirmed' || tx.paid) {
                            return { label: tx.paid ? (t("paid", "dashboard") || "Paid") : (t("confirmed", "dashboard") || "Confirmed"), variant: "success", icon: Check };
                          }
                          if (isBefore(txDate, today) && !isToday(txDate)) {
                            return { label: t("overdue", "dashboard") || "Overdue", variant: "destructive", icon: AlertTriangle };
                          }
                          if (isToday(txDate)) {
                            return { label: t("due_today", "dashboard") || "Due Today", variant: "warning", icon: Clock };
                          }
                          return { label: t("upcoming", "dashboard") || "Upcoming", variant: "info", icon: Calendar };
                        };

                        const status = getStatus();
                        const StatusIcon = status.icon;
                        const originalSymbol = getCurrencySymbol(tx.currency || 'USD');

                        return (
                          <TableRow
                            key={tx.id}
                            className={`${tx.type === 'income' ? 'hover:bg-green-50/50 dark:hover:bg-green-950/20' : 'hover:bg-red-50/50 dark:hover:bg-red-950/20'} ${tx.status === 'skipped' ? 'opacity-50' : ''} ${selectedRows.has(tx.id) ? 'bg-muted/30' : ''}`}
                            data-testid={`transaction-row-${tx.id}`}
                          >
                            <TableCell className="w-[50px]">
                              <Checkbox
                                checked={selectedRows.has(tx.id)}
                                onCheckedChange={() => toggleRowSelection(tx.id)}
                                aria-label={`Select ${tx.name}`}
                                data-testid={`select-row-${tx.id}`}
                              />
                            </TableCell>
                            {visibleColumns.date && (
                              <TableCell className="font-medium">
                                {formatDateUTC(tx.date)}
                              </TableCell>
                            )}
                            {visibleColumns.name && (
                              <TableCell>
                                <span className="font-medium">{tx.name || "Unnamed"}</span>
                                {tx.description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-[200px]">{tx.description}</p>
                                )}
                              </TableCell>
                            )}
                            {visibleColumns.category && (
                              <TableCell>
                                {tx.category_name ? (
                                  <Badge variant="outline">{tx.category_name}</Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}}
                              </TableCell>
                            )}
                            {visibleColumns.type && (
                              <TableCell>
                                <Badge className={tx.type === 'income' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}>
                                  {tx.type === 'income' ? (t("income", "dashboard") || "Income") : (t("expense", "dashboard") || "Expense")}
                                </Badge>
                              </TableCell>
                            )}
                            {visibleColumns.original && (
                              <TableCell className={`text-right font-medium font-numeric ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.type === 'income' ? '+' : '-'}{originalSymbol}{tx.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                            )}
                            {preferredCurrency && visibleColumns.converted && (
                              <TableCell className={`text-right font-medium font-numeric ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.converted_amount != null ? (
                                  <span>
                                    {tx.type === 'income' ? '+' : '-'}{getCurrencySymbol(preferredCurrency)}{tx.converted_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            )}
                            {visibleColumns.balance && (
                              <TableCell className={`text-right font-medium font-numeric ${tx.runningBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}{tx.runningBalance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                            )}
                            {visibleColumns.status && (
                              <TableCell>
                                <Badge variant="outline" className="gap-1">
                                  <StatusIcon className="h-3 w-3" />
                                  {status.label}
                                </Badge>
                              </TableCell>
                            )}
                            {visibleColumns.actions && (
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEditTransaction(tx)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      {t("edit", "common") || "Edit"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={async () => {
                                        if (window.confirm(t("confirm_delete", "common") || "Are you sure?")) {
                                          try {
                                            await api.delete(`/transactions/${tx.id}`);
                                            toast.success(t("transaction_deleted", "dashboard") || "Transaction deleted");
                                            fetchTransactionData();
                                          } catch (error) {
                                            toast.error(t("failed_to_delete", "errors") || "Failed to delete");
                                          }
                                        }
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      {t("delete", "common") || "Delete"}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Selection Summary - Only visible when rows are selected */}
              {selectedRows.size > 0 && (
                <div className="mt-4 p-4 bg-muted/30 rounded-lg border" data-testid="selection-summary">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium">
                      {getSelectionTotals().count} {getSelectionTotals().count === 1 ? 'row' : 'rows'} selected
                    </p>
                    <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-selection">
                      <X className="h-4 w-4 mr-1" />
                      {t("clear", "common") || "Clear"}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Selected Income */}
                    <div className="p-3 bg-background rounded-lg border">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">{t("selected_income", "dashboard") || "Selected Income"}</p>
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      </div>
                      <p className="text-lg font-bold mt-1 font-numeric text-green-600">
                        {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                        {getSelectionTotals().totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    
                    {/* Selected Expenses */}
                    <div className="p-3 bg-background rounded-lg border">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">{t("selected_expenses", "dashboard") || "Selected Expenses"}</p>
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      </div>
                      <p className="text-lg font-bold mt-1 font-numeric text-red-600">
                        {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                        {getSelectionTotals().totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    
                    {/* Selected Balance */}
                    <div className="p-3 bg-background rounded-lg border">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">{t("selected_balance", "dashboard") || "Selected Balance"}</p>
                        <DollarSign className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <p className={`text-lg font-bold mt-1 font-numeric ${getSelectionTotals().balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD')}
                        {getSelectionTotals().balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {/* ==================== END TRANSACTIONS SECTION ==================== */}
      </main>
      
      {/* Floating Quick Actions Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t shadow-lg" data-testid="floating-quick-actions">
        <div className="max-w-[1400px] mx-auto px-4 py-3">
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => navigate("/sales?action=new")} data-testid="quick-new-sale">
              <ShoppingCart className="h-4 w-4" />
              <span className="hidden sm:inline">{t("record_sale", "company") || "Record Sale"}</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => navigate("/clients?action=new")} data-testid="quick-new-client">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">{t("add_client", "company") || "Add Client"}</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => navigate("/products-services?action=new")} data-testid="quick-new-product">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">{t("add_product", "company") || "Add Product"}</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => navigate("/objectives?action=new")} data-testid="quick-new-objective">
              <Target className="h-4 w-4" />
              <span className="hidden sm:inline">{t("set_objective", "company") || "Set Objective"}</span>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Transaction Dialog */}
      <TransactionDialog
        open={showTransactionDialog}
        onOpenChange={setShowTransactionDialog}
        categories={categories}
        transaction={editingTransaction}
        onSuccess={() => {
          setShowTransactionDialog(false);
          setEditingTransaction(null);
          fetchTransactionData();
        }}
        currencies={currencies}
        userSettings={userSettings}
        companySettings={companySettings}
      />
      
      {/* Category Dialog */}
      <CategoryDialog
        open={showCategoryDialog}
        onOpenChange={setShowCategoryDialog}
        categories={categories}
        onSuccess={() => {
          fetchTransactionData();
        }}
      />
    </div>
  );
}

// Transaction Dialog Component
function TransactionDialog({ open, onOpenChange, categories, transaction, onSuccess, currencies, userSettings, companySettings }) {
  const { t } = useLanguage();
  const isEditing = !!transaction;
  const [isLoading, setIsLoading] = useState(false);
  const [transactionNames, setTransactionNames] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const defaultCurrency = userSettings?.default_currency || "USD";
  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    amount: "",
    currency: defaultCurrency,
    description: "",
    date: new Date().toISOString().split("T")[0],
    type: "expense",
    paid: false,
    is_recurring: false,
    is_optional: false,
    frequency: "monthly",
    base_due_date: 1,
    day_of_week: new Date().getDay() === 0 ? 6 : new Date().getDay() - 1,
    recurring_month: new Date().getMonth() + 1,
    payment_method_id: "",
    source_payment_method_id: "",
    destination_payment_method_id: ""
  });

  const paymentMethods = companySettings?.payment_methods?.filter(pm => pm.enabled) || [];
  const groupedPaymentMethods = {
    gateway: paymentMethods.filter(pm => pm.type === 'gateway'),
    bank: paymentMethods.filter(pm => pm.type === 'bank'),
    cash: paymentMethods.filter(pm => pm.type === 'cash'),
    check: paymentMethods.filter(pm => pm.type === 'check'),
    other: paymentMethods.filter(pm => pm.type === 'other')
  };

  const getPaymentMethodIcon = (type) => {
    switch (type) {
      case 'gateway': return <CreditCard className="h-4 w-4" />;
      case 'bank': return <Building2 className="h-4 w-4" />;
      case 'cash': return <Banknote className="h-4 w-4" />;
      case 'check': return <Receipt className="h-4 w-4" />;
      default: return <Wallet className="h-4 w-4" />;
    }
  };

  const formatPaymentMethodName = (pm) => {
    if (pm.type === 'bank' && pm.bank_name && pm.account_number_last4) {
      return `${pm.name} (${pm.bank_name} ****${pm.account_number_last4})`;
    }
    return pm.name;
  };

  const daysOfWeek = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" },
    { value: 5, label: "Saturday" },
    { value: 6, label: "Sunday" }
  ];

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  useEffect(() => {
    if (open) {
      api.get("/transaction-names").then(res => {
        setTransactionNames(res.data || []);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (transaction) {
      const currentDay = new Date().getDay();
      const pythonDay = currentDay === 0 ? 6 : currentDay - 1;
      
      setFormData({
        name: transaction.name || "",
        category_id: transaction.category_id || "",
        amount: transaction.original_amount || transaction.amount || "",
        currency: transaction.currency || defaultCurrency,
        description: transaction.description || "",
        date: transaction.date?.split("T")[0] || new Date().toISOString().split("T")[0],
        type: transaction.type || "expense",
        paid: transaction.paid || false,
        is_recurring: transaction.is_recurring || false,
        is_optional: transaction.is_optional || false,
        frequency: transaction.frequency || "monthly",
        base_due_date: transaction.base_due_date || 1,
        day_of_week: transaction.day_of_week ?? pythonDay,
        recurring_month: transaction.recurring_month || new Date().getMonth() + 1,
        payment_method_id: transaction.payment_method_id || "",
        source_payment_method_id: transaction.source_payment_method_id || "",
        destination_payment_method_id: transaction.destination_payment_method_id || ""
      });
    } else {
      const currentDay = new Date().getDay();
      const pythonDay = currentDay === 0 ? 6 : currentDay - 1;
      
      setFormData({
        name: "",
        category_id: "",
        amount: "",
        currency: defaultCurrency,
        description: "",
        date: new Date().toISOString().split("T")[0],
        type: "expense",
        paid: false,
        is_recurring: false,
        is_optional: false,
        frequency: "monthly",
        base_due_date: 1,
        day_of_week: pythonDay,
        recurring_month: new Date().getMonth() + 1,
        payment_method_id: "",
        source_payment_method_id: "",
        destination_payment_method_id: ""
      });
    }
  }, [transaction, open, defaultCurrency]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.amount) {
      toast.error("Name and amount are required");
      return;
    }

    if (formData.type === 'transfer') {
      if (!formData.source_payment_method_id || !formData.destination_payment_method_id) {
        toast.error("Transfer requires both source and destination accounts");
        return;
      }
      if (formData.source_payment_method_id === formData.destination_payment_method_id) {
        toast.error("Source and destination accounts cannot be the same");
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: formData.category_id || null,
        base_due_date: formData.frequency === "weekly" ? undefined : formData.base_due_date,
        day_of_week: formData.frequency === "weekly" ? formData.day_of_week : undefined,
        recurring_month: formData.frequency === "yearly" ? formData.recurring_month : undefined,
        payment_method_id: formData.type !== 'transfer' ? (formData.payment_method_id || null) : null,
        source_payment_method_id: formData.type === 'transfer' ? formData.source_payment_method_id : null,
        destination_payment_method_id: formData.type === 'transfer' ? formData.destination_payment_method_id : null
      };

      if (isEditing) {
        await api.put(`/transactions/${transaction.id}`, payload);
        toast.success(t("transaction_updated", "dashboard") || "Transaction updated");
      } else {
        await api.post("/transactions", payload);
        toast.success(t("transaction_added", "dashboard") || "Transaction added");
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSuggestions = transactionNames.filter(name =>
    name.toLowerCase().includes(formData.name.toLowerCase()) && name !== formData.name
  ).slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? (t("edit_transaction", "dashboard") || "Edit Transaction") : (t("add_new_transaction", "dashboard") || "Add New Transaction")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the transaction details" : "Add a new income or expense transaction"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {/* Type Selection */}
          <div className="space-y-2">
            <Label>{t("type", "dashboard") || "Type"}</Label>
            <RadioGroup
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value, payment_method_id: "", source_payment_method_id: "", destination_payment_method_id: "" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="expense" id="tx-expense" />
                <Label htmlFor="tx-expense" className="cursor-pointer">{t("expense", "dashboard") || "Expense"}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="income" id="tx-income" />
                <Label htmlFor="tx-income" className="cursor-pointer">{t("income", "dashboard") || "Income"}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="transfer" id="tx-transfer" />
                <Label htmlFor="tx-transfer" className="cursor-pointer flex items-center gap-1">
                  <ArrowRightLeft className="h-3 w-3" /> Transfer
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Name with autocomplete */}
          <div className="space-y-2 relative">
            <Label>{t("transaction_name", "dashboard") || "Transaction Name"}</Label>
            <Input
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setShowNameSuggestions(true);
              }}
              onFocus={() => setShowNameSuggestions(true)}
              onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
              placeholder="e.g., Office Supplies, Client Payment"
              data-testid="transaction-name-input"
            />
            {showNameSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-popover border rounded-md shadow-md mt-1 max-h-32 overflow-y-auto">
                {filteredSuggestions.map((name, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                    onMouseDown={() => {
                      setFormData({ ...formData, name });
                      setShowNameSuggestions(false);
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Amount and Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("amount", "dashboard") || "Amount"}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                data-testid="transaction-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("currency", "dashboard") || "Currency"}</Label>
              <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                <SelectTrigger data-testid="transaction-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(userSettings?.enabled_currencies || ["USD"]).map((code) => {
                    const curr = currencies.find(c => c.code === code);
                    return curr ? (
                      <SelectItem key={code} value={code}>{curr.symbol} {code} - {curr.name}</SelectItem>
                    ) : null;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("category", "dashboard") || "Category"}</Label>
              <Select value={formData.category_id || "none"} onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="transaction-category-select">
                  <SelectValue placeholder={t("select_category", "dashboard") || "Select category"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("no_category", "dashboard") || "No category"}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("date", "dashboard") || "Date"}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                data-testid="transaction-date-input"
              />
            </div>
          </div>

          {/* Payment Method - For Income/Expense */}
          {formData.type !== 'transfer' && paymentMethods.length > 0 && (
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={formData.payment_method_id || "none"} onValueChange={(v) => setFormData({ ...formData, payment_method_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="transaction-payment-method-select">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No payment method</SelectItem>
                  {groupedPaymentMethods.gateway.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2"><CreditCard className="h-3 w-3" /> Payment Gateways</SelectLabel>
                      {groupedPaymentMethods.gateway.map(pm => (
                        <SelectItem key={pm.id} value={pm.id}>
                          <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {formatPaymentMethodName(pm)}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {groupedPaymentMethods.bank.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2"><Building2 className="h-3 w-3" /> Bank Accounts</SelectLabel>
                      {groupedPaymentMethods.bank.map(pm => (
                        <SelectItem key={pm.id} value={pm.id}>
                          <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {formatPaymentMethodName(pm)}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {groupedPaymentMethods.cash.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2"><Banknote className="h-3 w-3" /> Cash</SelectLabel>
                      {groupedPaymentMethods.cash.map(pm => (
                        <SelectItem key={pm.id} value={pm.id}>
                          <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {pm.name}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {groupedPaymentMethods.check.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2"><Receipt className="h-3 w-3" /> Check</SelectLabel>
                      {groupedPaymentMethods.check.map(pm => (
                        <SelectItem key={pm.id} value={pm.id}>
                          <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {pm.name}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {groupedPaymentMethods.other.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2"><Wallet className="h-3 w-3" /> Other</SelectLabel>
                      {groupedPaymentMethods.other.map(pm => (
                        <SelectItem key={pm.id} value={pm.id}>
                          <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {pm.name}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Transfer Accounts - For Transfer type */}
          {formData.type === 'transfer' && paymentMethods.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><TrendingDown className="h-3 w-3 text-red-500" /> {t("from_account", "company") || "From Account"}</Label>
                <Select value={formData.source_payment_method_id || ""} onValueChange={(v) => setFormData({ ...formData, source_payment_method_id: v })}>
                  <SelectTrigger data-testid="transaction-source-account-select">
                    <SelectValue placeholder="Select source account" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(pm => (
                      <SelectItem key={pm.id} value={pm.id} disabled={pm.id === formData.destination_payment_method_id}>
                        <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {formatPaymentMethodName(pm)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><TrendingUp className="h-3 w-3 text-green-500" /> {t("to_account", "company") || "To Account"}</Label>
                <Select value={formData.destination_payment_method_id || ""} onValueChange={(v) => setFormData({ ...formData, destination_payment_method_id: v })}>
                  <SelectTrigger data-testid="transaction-destination-account-select">
                    <SelectValue placeholder="Select destination account" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(pm => (
                      <SelectItem key={pm.id} value={pm.id} disabled={pm.id === formData.source_payment_method_id}>
                        <span className="flex items-center gap-2">{getPaymentMethodIcon(pm.type)} {formatPaymentMethodName(pm)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Transfer info message */}
          {formData.type === 'transfer' && paymentMethods.length === 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              No payment methods configured. Go to Settings to add payment methods before creating transfers.
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label>{t("description", "dashboard") || "Description"}</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t("note_placeholder", "common") || "Add a note"}
              data-testid="transaction-description-input"
            />
          </div>

          {/* Paid Status - Hide for transfers */}
          {formData.type !== 'transfer' && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <Label>{formData.type === "income" ? "Received" : "Paid"}</Label>
              <Switch
                checked={formData.paid}
                onCheckedChange={(checked) => setFormData({ ...formData, paid: checked })}
                data-testid="transaction-paid-switch"
              />
            </div>
          )}

          {/* Recurring Options */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <Label>{t("is_recurring", "dashboard") || "Is Recurring"}</Label>
            <Switch
              checked={formData.is_recurring}
              onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
              data-testid="transaction-recurring-switch"
            />
          </div>

          {formData.is_recurring && (
            <div className="space-y-4 p-3 border rounded-lg">
              <div className="space-y-2">
                <Label>{t("frequency", "dashboard") || "Frequency"}</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{t("weekly", "dashboard") || "Weekly"}</SelectItem>
                    <SelectItem value="monthly">{t("monthly", "dashboard") || "Monthly"}</SelectItem>
                    <SelectItem value="yearly">{t("yearly", "dashboard") || "Yearly"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.frequency === "weekly" && (
                <div className="space-y-2">
                  <Label>{t("day_of_week", "dashboard") || "Day of Week"}</Label>
                  <Select value={String(formData.day_of_week)} onValueChange={(v) => setFormData({ ...formData, day_of_week: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {daysOfWeek.map(d => (
                        <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.frequency === "monthly" && (
                <div className="space-y-2">
                  <Label>{t("day_of_month", "dashboard") || "Day of Month"}</Label>
                  <Select value={String(formData.base_due_date)} onValueChange={(v) => setFormData({ ...formData, base_due_date: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.frequency === "yearly" && (
                <>
                  <div className="space-y-2">
                    <Label>{t("month", "dashboard") || "Month"}</Label>
                    <Select value={String(formData.recurring_month)} onValueChange={(v) => setFormData({ ...formData, recurring_month: parseInt(v) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(m => (
                          <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("day_of_month", "dashboard") || "Day of Month"}</Label>
                    <Select value={String(formData.base_due_date)} onValueChange={(v) => setFormData({ ...formData, base_due_date: parseInt(v) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">{t("optional_recurring", "dashboard") || "Optional (requires confirmation)"}</Label>
                </div>
                <Switch
                  checked={formData.is_optional}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_optional: checked })}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t("cancel", "common") || "Cancel"}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading} data-testid="save-transaction-button">
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditing ? (t("update", "common") || "Update") : (t("save", "common") || "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Category Management Dialog
function CategoryDialog({ open, onOpenChange, categories, onSuccess }) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: "", type: "expense" });

  const handleAddCategory = async () => {
    if (!categoryForm.name) return;
    
    setIsLoading(true);
    try {
      await api.post("/categories", categoryForm);
      toast.success("Category created");
      setCategoryForm({ name: "", type: "expense" });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create category");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Category deleted");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="category-dialog">
        <DialogHeader>
          <DialogTitle>{t("category_management", "dashboard") || "Category Management"}</DialogTitle>
          <DialogDescription>{t("manage_categories_desc", "dashboard") || "Create and manage your transaction categories"}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Add Category Form */}
          <div className="flex gap-2">
            <Input
              placeholder={t("category_name", "dashboard") || "Category name"}
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              data-testid="new-category-input"
            />
            <Select value={categoryForm.type} onValueChange={(v) => setCategoryForm({ ...categoryForm, type: v })}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">{t("expense", "dashboard") || "Expense"}</SelectItem>
                <SelectItem value="income">{t("income", "dashboard") || "Income"}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleAddCategory} disabled={isLoading || !categoryForm.name} data-testid="add-category-button">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>

          {/* Category List */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {categories.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                {t("no_categories", "dashboard") || "No categories yet"}
              </p>
            ) : (
              categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cat.type === 'income' ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                      {cat.type}
                    </Badge>
                    <span>{cat.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteCategory(cat.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("close", "common") || "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
